import React, { useEffect } from 'react';

import { AUTH_CONFIG } from '../../auth0-config';
import { AppContext } from '../../App';
import { useNavigate, Link } from 'react-router';
import { useAuth0 } from '@auth0/auth0-react';
import useUserDataPools from '../hooks/useUserDataPools';
import { clearSessionStorage } from '../../utils/localStorageUtils';

import logo from '../../images/Logo_GLYNT_2020_75x25.png';

import { collapseString } from '../../utils/collapseString';
import { featureFlags } from '../../utils/featureFlags';

import { Tooltip, Menu, MenuItem, useTheme } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Container from '@mui/material/Container';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import MenuIcon from '@mui/icons-material/Menu';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import LogoutIcon from '@mui/icons-material/Logout';

const mobileDrawerWidth = 220;
const mobileBreakpoint = 1030;

const TopBar = () => {
  const { orgId, orgLabel, dataPoolLabel, dataPoolId, isStaff, setMaintenance } =
    React.useContext(AppContext);

  const { logout } = useAuth0();
  const navigate = useNavigate();
  const theme = useTheme();

  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const dp = useUserDataPools();
  const orgDataPools = dp?.orgDataPools || [];

  useEffect(() => {
    if (localStorage.getItem('maintenance')) {
      setMaintenance(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let navItems = [
    {
      name: 'Vendors',
      link: `/vendors`,
      requireDataPool: false,
      isStaff: true,
      basicUserOnly: false,
      matchOn: '/vendors',
      dataCyDesktop: 'menu-vendors',
      dataCyMobile: 'menu-mobile-vendors',
    },
    {
      name: 'Organizations',
      link: `/organization/${orgId ? orgId : ''}`,
      requireDataPool: false,
      isStaff: true,
      basicUserOnly: false,
      matchOn: '^/organization/',
      dataCyDesktop: 'menu-organizations',
      dataCyMobile: 'menu-mobile-organizations',
    },
    {
      name: 'Data Pools',
      link: `/selectDefault`,
      requireDataPool: false,
      isStaff: false,
      basicUserOnly: true,
      matchOn: '^/selectDefault',
      dataCyDesktop: 'menu-data-pools',
      dataCyMobile: 'menu-mobile-data-pools',
    },
    {
      name: 'Documents',
      link: `/data-pools/${dataPoolId}/documents`,
      requireDataPool: true,
      isStaff: false,
      basicUserOnly: false,
      matchOn: '/(documents|data-exports)',
      dataCyDesktop: 'menu-documents',
      dataCyMobile: 'menu-mobile-documents',
    },
    {
      name: 'Train',
      link: `/data-pools/${dataPoolId}/train`,
      requireDataPool: true,
      isStaff: false,
      basicUserOnly: false,
      matchOn: '/(train|mark|fields|manage)',
      dataCyDesktop: 'menu-train',
      dataCyMobile: 'menu-mobile-train',
    },
    {
      name: 'Extraction Batches',
      link: `/data-pools/${dataPoolId}/extraction-batches`,
      requireDataPool: true,
      isStaff: false,
      basicUserOnly: false,
      matchOn: '^/$|/(extraction-batches|extraction-batch/|review)',
      dataCyDesktop: 'menu-extraction-batches',
      dataCyMobile: 'menu-mobile-extraction-batches',
    },
  ];

  if (featureFlags.showPackagerMenuItem) {
    navItems.push({
      name: 'Packager',
      link: `/data-pools/${dataPoolId}/packager`,
      requireDataPool: true,
      isStaff: true,
      basicUserOnly: false,
      matchOn: '/packager',
      dataCyDesktop: 'menu-packager',
      dataCyMobile: 'menu-mobile-packager',
    });
  }

  if (featureFlags.MATMenuItem) {
    navItems.push({
      name: 'MAT',
      link: `/data-pools/${dataPoolId}/mat`,
      requireDataPool: true,
      isStaff: true,
      basicUserOnly: false,
      matchOn: '/mat',
      dataCyDesktop: 'menu-mat',
      dataCyMobile: 'menu-mobile-mat',
    });
  }

  const handleDrawerToggle = () => {
    setMobileOpen((prevState) => !prevState);
  };

  function handleLogout() {
    clearSessionStorage();
    logout({ logoutParams: { returnTo: `${AUTH_CONFIG.hostUrl}/logout` } });
  }

  // FIXME Memoize
  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
      <Box sx={{ my: 2 }}>
        <img src={logo} width={75} alt="Logo" />
      </Box>
      <Divider />
      <List>
        {dp &&
          navItems.map((item) => {
            const isActive = item.matchOn
              ? new RegExp(item.matchOn).test(location.pathname)
              : false;

            if (item.requireDataPool && !dataPoolId) {
              return null;
            }

            if (item.isStaff && !isStaff) {
              return null;
            }

            if (item.basicUserOnly && isStaff) {
              return null;
            }

            return (
              <ListItem key={item.name} disablePadding>
                <ListItemButton
                  component={Link}
                  to={item.link}
                  sx={{ textAlign: 'center' }}
                  data-cy={item.dataCyMobile}
                >
                  <ListItemText
                    primary={item.name}
                    sx={{
                      color: isActive ? theme.palette.custom.lightGreen : 'inherit',
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}

        <ListItem key={'Logout'} disablePadding>
          <ListItemButton sx={{ textAlign: 'center' }}>
            <ListItemText primary={'Logout'} onClick={() => handleLogout} />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  const container = window !== undefined ? () => window.document.body : undefined;

  const sharedMenuItemStyles = {
    my: 2,
    color: 'black',
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: 'transparent',
      boxShadow: 'none',
      color: theme.palette.custom.lightGreen,
    },
  };

  const activeMenuItemStyles = {
    ...sharedMenuItemStyles,
    color: theme.palette.custom.lightGreen,
  };

  const orgDataPoolSelector = (method: 'desktop' | 'mobile') => {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            marginRight: '10px',
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <Typography
              style={{
                fontSize: '13px',
              }}
              color="inherit"
              align="right"
            >
              {orgLabel}
            </Typography>

            <Tooltip title={dataPoolLabel} placement="top">
              <Typography
                onClick={handleClick}
                style={{
                  cursor: 'pointer',
                  fontSize: '13px',
                  padding: 0,
                  margin: 0,
                  marginBlockStart: 0,
                  marginBlockEnd: 0,
                }}
                color="inherit"
                align="right"
              >
                {dataPoolLabel ? collapseString(dataPoolLabel, 14, 15) : ''}
              </Typography>
            </Tooltip>
            <Menu
              id="basic-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              MenuListProps={{
                'aria-labelledby': 'basic-button',
              }}
            >
              {orgDataPools?.map((dt: any) => {
                if (dt.id === dataPoolId) {
                  return null;
                }

                return (
                  <MenuItem
                    key={dt.id}
                    onClick={() => {
                      navigate(`/data-pools/${dt.id}/extraction-batches`);
                    }}
                    sx={{ fontSize: '12px' }}
                  >
                    {collapseString(dt.label, 17, 17)}
                  </MenuItem>
                );
              })}
            </Menu>
          </div>
        </Box>

        <Box
          sx={{
            p: 0,
          }}
        >
          <IconButton onClick={handleLogout} data-cy={`menu-logout-${method}`}>
            <LogoutIcon sx={{ color: 'black', height: '20px' }} />
          </IconButton>
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      <AppBar
        component="nav"
        position="static"
        sx={{
          backgroundColor: 'white',
          borderBottom: '1px solid #BDC3D2',
          boxShadow: 'none',
          color: 'black',
        }}
      >
        <Container maxWidth={false}>
          <Toolbar disableGutters>
            {/* DESKTOP MENU */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
                [`@media (max-width: ${mobileBreakpoint}px)`]: {
                  display: 'none',
                },
              }}
            >
              {/* Logo/Menu Items */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {/* Logo */}
                <Box
                  component="a"
                  href="/"
                  sx={{
                    mr: 3,
                    ml: 0,
                  }}
                >
                  <img src={logo} width={75} alt="Glynt Logo" />
                </Box>

                {/* Desktop Menu Items */}
                <Box
                  sx={{
                    display: 'flex',
                  }}
                >
                  {dp &&
                    navItems.map((item) => {
                      const isActive = item.matchOn
                        ? new RegExp(item.matchOn).test(location.pathname)
                        : false;

                      if (item.requireDataPool && !dataPoolId) {
                        return null;
                      }

                      if (item.isStaff && !isStaff) {
                        return null;
                      }

                      if (item.basicUserOnly && isStaff) {
                        return null;
                      }

                      return (
                        <div key={item.name}>
                          <Link
                            to={item.link}
                            onClick={(e) => {
                              e.preventDefault();
                              navigate(item.link);
                            }}
                            data-cy={item.dataCyDesktop}
                            style={{
                              padding: '8px 10px',
                              textTransform: 'uppercase',
                              textDecoration: 'none',
                              fontFamily: 'Roboto, sans-serif',
                              fontSize: '.875rem',
                              color: isActive ? theme.palette.custom.lightGreen : 'black',
                              fontWeight: '500',
                              lineHeight: '1.75',
                            }}
                          >
                            {item.name}
                          </Link>
                        </div>
                      );
                    })}
                </Box>
              </Box>

              {/* Org/Data Pool Selector & Logout */}
              {orgDataPoolSelector('desktop')}
            </Box>

            {/* MOBILE MENU */}
            <Box
              sx={{
                display: 'flex',
                width: '100%',
                alignItems: 'center',
                justifyContent: 'space-between',
                [`@media (min-width: ${mobileBreakpoint + 1}px)`]: {
                  display: 'none',
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="start"
                  onClick={handleDrawerToggle}
                  sx={{
                    mr: 2,
                  }}
                >
                  <MenuIcon sx={{ color: '#112233' }} />
                </IconButton>
                <Box component="a" href="/">
                  <img src={logo} width={75} alt="Glynt Logo" />
                </Box>
              </Box>
              {orgDataPoolSelector('mobile')}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
      <nav>
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            [`@media (min-width: ${mobileBreakpoint}px)`]: {
              display: 'none',
            },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: mobileDrawerWidth },
          }}
        >
          {drawer}
        </Drawer>
      </nav>
    </Box>
  );
};

export default TopBar;
